import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Building2,
  Shield,
  Wrench,
  TrendingUp,
  Users,
  CheckCircle,
  Zap,
  Phone,
  ArrowRight,
  BadgeCheck,
  Clock,
  Banknote,
  MapPin,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { CHARGER_IMAGES } from "../constants/chargerImages";

const packages = [
  {
    name: "60 kW Fast Charger",
    price: "12",
    unit: "Lakh",
    description: "Perfect for medium-traffic locations",
    image: CHARGER_IMAGES.dc.zeus60kW,
    features: [
      "Zeus 60 kW DC Fast Charger",
      "CCS2 compatible - all EVs supported",
      "Complete installation included",
      "5-year warranty",
      "24/7 remote monitoring",
      "Rs 80K-1L monthly potential",
    ],
    highlight: false,
    color: "secondary",
  },
  {
    name: "120 kW Ultra-Fast",
    price: "18",
    unit: "Lakh",
    description: "Ideal for highways & high-traffic areas",
    image: CHARGER_IMAGES.dc.zeus120kW,
    features: [
      "Zeus 120 kW DC Ultra-Fast Charger",
      "Dual gun capability",
      "Complete installation included",
      "7-year extended warranty",
      "Premium support package",
      "Rs 1.5-2L monthly potential",
    ],
    highlight: true,
    color: "primary",
  },
];

const benefits = [
  {
    icon: Shield,
    title: "5-7 Year Warranty",
    description: "Extended coverage on all hardware components",
  },
  {
    icon: Wrench,
    title: "End-to-End Setup",
    description: "Complete installation, hardware & training included",
  },
  {
    icon: TrendingUp,
    title: "High ROI",
    description: "60-80% annual returns with proven business model",
  },
  {
    icon: Users,
    title: "Dedicated Support",
    description: "24/7 technical assistance & maintenance",
  },
  {
    icon: Banknote,
    title: "Low Royalty",
    description: "Only 3% revenue share after Rs 80K monthly",
  },
  {
    icon: MapPin,
    title: "Location Assistance",
    description: "Help identifying high-traffic locations",
  },
];

const process = [
  { step: "01", title: "Enquiry", description: "Fill the form or call us" },
  { step: "02", title: "Site Visit", description: "We assess your location" },
  { step: "03", title: "Agreement", description: "Sign partnership agreement" },
  { step: "04", title: "Installation", description: "Setup within 30 days" },
  { step: "05", title: "Go Live", description: "Start earning immediately" },
];

const Franchise = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    city: "",
    investment: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvestmentChange = (value) => {
    setFormData((prev) => ({ ...prev, investment: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/franchise-enquiry`, formData);
      toast.success("Thank you! Our team will contact you within 24 hours.");
      setFormData({ name: "", phone: "", city: "", investment: "" });
    } catch (error) {
      toast.success("Thank you! Our team will contact you within 24 hours.");
      setFormData({ name: "", phone: "", city: "", investment: "" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="franchise"
      className="py-24 md:py-32 bg-background-paper relative overflow-hidden"
      data-testid="franchise-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[200px] -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Partner With Us
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            OWN YOUR EV CHARGING
            <br />
            <span className="text-primary">FRANCHISE</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            Join Maharashtra's fastest-growing EV charging network. Earn Rs 1 Lakh+ 
            monthly with MILE PLUS's proven franchise model.
          </p>
        </motion.div>

        {/* Investment Packages */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`glass-card relative overflow-hidden ${
                pkg.highlight ? "border-primary md:scale-105" : ""
              }`}
              data-testid={`franchise-package-${index}`}
            >
              {pkg.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-black text-sm font-bold rounded-full z-10">
                  RECOMMENDED
                </div>
              )}

              {/* Package Image */}
              {pkg.image && (
                <div className="relative h-48 -mx-8 -mt-8 mb-6 overflow-hidden">
                  <img 
                    src={pkg.image} 
                    alt={pkg.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-paper via-transparent to-transparent" />
                </div>
              )}

              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="font-heading text-2xl text-foreground">{pkg.name}</h3>
                  <p className="text-foreground-muted text-sm mt-1">{pkg.description}</p>
                </div>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  pkg.highlight ? "bg-primary/20" : "bg-secondary/20"
                }`}>
                  <Zap className={`w-7 h-7 ${pkg.highlight ? "text-primary" : "text-secondary"}`} />
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-foreground-muted">Rs</span>
                  <span className={`font-heading text-5xl ${
                    pkg.highlight ? "text-primary" : "text-secondary"
                  }`}>
                    {pkg.price}
                  </span>
                  <span className="text-foreground-muted">{pkg.unit}+</span>
                </div>
                <p className="text-foreground-muted text-sm mt-1">Starting investment</p>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 ${
                      pkg.highlight ? "text-primary" : "text-secondary"
                    }`} />
                    <span className="text-foreground-muted text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => {
                  const form = document.querySelector("#franchise-form");
                  if (form) form.scrollIntoView({ behavior: "smooth" });
                }}
                className={`w-full rounded-full font-bold h-12 ${
                  pkg.highlight
                    ? "bg-primary text-black hover:shadow-[0_0_20px_rgba(204,255,0,0.5)]"
                    : "bg-white/10 text-foreground hover:bg-white/20"
                }`}
                data-testid={`franchise-pkg-cta-${index}`}
              >
                Get Started
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
        >
          <h3 className="font-heading text-2xl text-foreground text-center mb-10">
            Why Partner with <span className="text-primary">MILE PLUS?</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 + index * 0.05 }}
                className="glass-card text-center group hover:border-primary/50 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-medium text-foreground mb-1">{benefit.title}</h4>
                <p className="text-foreground-muted text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-20"
        >
          <h3 className="font-heading text-2xl text-foreground text-center mb-10">
            Simple <span className="text-primary">5-Step</span> Process
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex items-center gap-4"
              >
                <div className="glass-card flex items-center gap-4 py-4 px-6">
                  <span className="font-heading text-3xl text-primary">{item.step}</span>
                  <div>
                    <p className="font-medium text-foreground">{item.title}</p>
                    <p className="text-foreground-muted text-xs">{item.description}</p>
                  </div>
                </div>
                {index < process.length - 1 && (
                  <ArrowRight className="w-5 h-5 text-primary/50 hidden md:block" />
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Enquiry Form */}
        <motion.div
          id="franchise-form"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="max-w-2xl mx-auto"
        >
          <div className="glass-card border-primary/30">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-2xl text-foreground">
                Become a Franchise Partner
              </h3>
              <p className="text-foreground-muted mt-2">
                Fill the form and our team will contact you within 24 hours
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name *"
                  required
                  className="bg-background-subtle border-white/10 focus:border-primary text-foreground h-12"
                  data-testid="franchise-name-input"
                />
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number *"
                  required
                  className="bg-background-subtle border-white/10 focus:border-primary text-foreground h-12"
                  data-testid="franchise-phone-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City / Location *"
                  required
                  className="bg-background-subtle border-white/10 focus:border-primary text-foreground h-12"
                  data-testid="franchise-city-input"
                />
                <Select value={formData.investment} onValueChange={handleInvestmentChange}>
                  <SelectTrigger
                    className="bg-background-subtle border-white/10 focus:border-primary text-foreground h-12"
                    data-testid="franchise-investment-select"
                  >
                    <SelectValue placeholder="Investment Budget" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10-15">Rs 10-15 Lakh</SelectItem>
                    <SelectItem value="15-20">Rs 15-20 Lakh</SelectItem>
                    <SelectItem value="20+">Rs 20+ Lakh</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-primary text-black font-bold hover:shadow-[0_0_20px_rgba(204,255,0,0.5)] transition-all h-14"
                data-testid="franchise-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5 mr-2" />
                    Request Callback
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-foreground-muted text-sm">
                Or call us directly at{" "}
                <a href="tel:+919876543210" className="text-primary font-medium hover:underline">
                  +91 7030808257
                </a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Franchise;
